import React, { useEffect, useRef, useState } from "react";
import GameBoard from "./GameBoard";
import { useAviator } from "../../store/aviator";
import AccessDenied from "../AccessDenied";
import { Assets } from "pixi.js";
import { urls } from "../../utils/urls";
import {
  Game_Global_Vars,
  initBet6,
  loadSound,
  processCashoutSuccessData,
  isCashoutSuccessResponse,
} from "../../utils";
import TopLogoBar from "../TopLogoBar";
import RuleModal from "../RuleDialog";
import SettingModal from "../SettingModal";
import HistoryModal from "../HistoryModal";
import CashoutSuccessModal from "../modals/CashoutSuccessModal";
import LoadingSpine from "../LoadingSpine";
import {
  getRemainingLoaderDelay,
  MIN_LOADING_SPINE_MS,
} from "../LoadingSpine/constants";

import { useSocket } from "../../providers/SocketProvider";

const Aviator = () => {
  const {
    isConnected,
    getMarketData,
    connect,
    currencyConfig,
    gameConfigReady,
  } = useSocket();

  const { aviatorState } = useAviator();
  const { gameData } = useSocket();
  const [loaded, setLoaded] = useState(false);
  const [assetsLoaded, setAssetsLoaded] = useState(false);
  const [socketReady, setSocketReady] = useState(false);
  const loadingStartedAtRef = useRef(Date.now());
  const [marketId] = useState<string | null>(null);

  const [bet6, setBet6] = useState<string[]>(initBet6);
  const [ruleModalOpen, setRuleModalOpen] = useState(false);
  const [settingModalOpen, setSettingModalOpen] = useState(false);
  const [historyModalOpen, setHistoryModalOpen] = useState(false);
  const [cashoutToasts, setCashoutToasts] = useState<
    Array<{
      id: string;
      data: { cashOutAtMultiplier: string; cashout: number; stake: number };
    }>
  >([]);

  React.useEffect(() => {
    Game_Global_Vars.betValue = [bet6[0], bet6[0]];
  }, [bet6]);

  React.useEffect(() => {
    Game_Global_Vars.stake.min = currencyConfig.minBet;
    Game_Global_Vars.stake.max = currencyConfig.maxBet;
  }, [currencyConfig.maxBet, currencyConfig.minBet]);

  // Track socket connection and game data ready
  React.useEffect(() => {
    if (isConnected && gameData && gameData.status) {
      setSocketReady(true);
    }
  }, [isConnected, gameData]);

  // Auto-start game when both assets and socket are ready
  React.useEffect(() => {
    if (assetsLoaded && socketReady && gameConfigReady) {
      const remainingDelay = getRemainingLoaderDelay(
        loadingStartedAtRef.current,
        MIN_LOADING_SPINE_MS,
      );
      const timeoutId = window.setTimeout(() => setLoaded(true), remainingDelay);

      return () => window.clearTimeout(timeoutId);
    }
  }, [assetsLoaded, gameConfigReady, socketReady]);

  React.useEffect(() => {
    let initialized = false;

    const initializeSound = () => {
      if (initialized) return;
      initialized = true;
      void loadSound();
      window.removeEventListener("pointerdown", initializeSound);
      window.removeEventListener("keydown", initializeSound);
    };

    window.addEventListener("pointerdown", initializeSound, { passive: true });
    window.addEventListener("keydown", initializeSound);

    return () => {
      window.removeEventListener("pointerdown", initializeSound);
      window.removeEventListener("keydown", initializeSound);
    };
  }, []);

  React.useEffect(() => {
    // Load assets with error handling
    if (urls.length === 0) {
      setAssetsLoaded(true);
      return;
    }
    Assets.load(urls)
      .then(() => {
        setAssetsLoaded(true);
      })
      .catch(() => {
        setAssetsLoaded(true);
      });

    connect("", {
      type: "1",
      id: "88.0022",
    });
  }, [connect]);

  useEffect(() => {
    if (!isConnected) return;

    return undefined;
  }, [isConnected, getMarketData]);


  // Handle cashout success
  const handleCashoutSuccess = (cashoutData: any) => {
    const processedData = processCashoutSuccessData(cashoutData);
    if (processedData) {
      const id = `${Date.now()}-${Math.random().toString(16).slice(2)}`;
      setCashoutToasts((prev) => [...prev, { id, data: processedData }]);
    }
  };

  // Handle API response for cashout success (used by child components)
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const _handleAPIResponse = (response: any) => {
    if (isCashoutSuccessResponse(response)) {
      handleCashoutSuccess(response);
    }
  };

  // To use this popup, call handleAPIResponse when you receive your API response:
  // handleAPIResponse(yourAPIResponseData);
  //
  // Example with your exact data format:
  // const apiResponse = {
  //   "data": [{
  //     "betId": "689cf2ec4b63cb03c6a33a5f",
  //     "userName": "t*********2",
  //     "stake": 100,
  //     "marketId": "1248123",
  //     "nation": "CASHOUT",
  //     "cashout": 127,
  //     "createdAt": "2025-08-13T20:17:48.422Z",
  //     "cashOutAtMultiplier": "1.27"
  //   }],
  //   "meta": {
  //     "message": "Casino Bet Cashout Successful.",
  //     "status_code": 200,
  //     "status": true
  //   }
  // };
  // handleAPIResponse(apiResponse);

  // Close cashout success modal
  const closeCashoutToast = React.useCallback((id: string) => {
    setCashoutToasts((prev) => prev.filter((toast) => toast.id !== id));
  }, []);

  return (
    <>
      <TopLogoBar
        loaded={loaded}
        setSettingModalOpen={setSettingModalOpen}
        setHistoryModalOpen={setHistoryModalOpen}
        setRuleModalOpen={setRuleModalOpen}
      />

      {/* Debug UI for development */}
      {/* {process.env.NODE_ENV === "development" && (
        <div
          style={{
            position: "fixed",
            top: 10,
            right: 10,
            background: "#333",
            color: "white",
            padding: "10px",
            borderRadius: "5px",
            fontSize: "12px",
            zIndex: 1000,
          }}
        >
          <div>WS: {wsConnected ? "🟢 Connected" : "🔴 Disconnected"}</div>
          <div>Market ID: {marketId || "None"}</div>
          <button
            onClick={refreshBalance}
            style={{
              marginTop: "5px",
              padding: "2px 8px",
              background: "#666",
              border: "none",
              color: "white",
              borderRadius: "3px",
              cursor: "pointer",
            }}
          >
            Refresh Balance
          </button>
        </div>
      )} */}
      {aviatorState.auth ? (
        loaded ? (
          <GameBoard
            bet6={bet6}
            marketId={marketId}
            onCashoutSuccess={handleCashoutSuccess}
          />
        ) : (
          <LoadingSpine />
        )
      ) : (
        <AccessDenied />
      )}
      <RuleModal open={ruleModalOpen} setOpen={setRuleModalOpen} />
      <SettingModal
        open={settingModalOpen}
        setOpen={setSettingModalOpen}
        bet6={{ bet6, setBet6 }}
      />
      <HistoryModal
        loaded={loaded}
        open={historyModalOpen}
        setOpen={setHistoryModalOpen}
      />
      {cashoutToasts.map((toast, index) => (
        <CashoutSuccessModal
          key={toast.id}
          isOpen={true}
          onClose={() => closeCashoutToast(toast.id)}
          cashoutData={toast.data}
          offset={index}
        />
      ))}
    </>
  );
};
export default Aviator;
